import { createElement, ElementType, FocusEventHandler } from 'react'
import { debounce, groupBy, has, isEmpty, isEqual, unionWith } from 'lodash'

import { DatasourceResponse, Element } from 'common/interfaces'

import {
  useDatasourceMutation,
  UseDatasourceMutationReturn,
  useDatasourceQuery,
  UseDatasourceQueryReturn,
} from './common'

function assign(
  queries: UseDatasourceQueryReturn | UseDatasourceMutationReturn
): Record<string, any> {
  let results: Array<NonNullable<DatasourceResponse['results']>>
  if (Array.isArray(queries)) {
    results = queries.map((query) => query.data?.props ?? {})
  } else {
    results = queries.data?.map((data) => data.props ?? {}) ?? []
  }

  return Object.assign({}, ...results)
}

function refetch(query: UseDatasourceQueryReturn[number]): void {
  void query.refetch()
}

interface DataSourceProps {
  node: Element
  Component: ElementType
}

export default function DataSource({ node, Component }: DataSourceProps): JSX.Element | null {
  const { events, name, props: elementProps } = node

  const eventsByType = groupBy(events, 'type')
  const {
    onload = [],
    onfocus = [],
    onclick = [],
    onchange = [],
    oninteraction = [],
  } = eventsByType

  const queriesOnLoad = useDatasourceQuery(onload, true, true)
  const queriesOnFocus = useDatasourceQuery(onfocus, false)
  const mutationOnClick = useDatasourceMutation(onclick)
  const mutationOnChange = useDatasourceMutation(onchange)
  const mutationOnInteraction = useDatasourceMutation(oninteraction)

  const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
    queriesOnFocus.forEach(refetch)
  }

  const onClick = mutationOnClick.mutate
  const onChange = !isEmpty(onchange) ? debounce(mutationOnChange.mutate, 500) : undefined
  const onInteraction = debounce(mutationOnInteraction.mutate, 500)

  if (!queriesOnLoad.every((query) => query.isFetched)) {
    return null
  }

  // change loading is not presented because onChange events
  // should not affect element itself
  const loading =
    mutationOnInteraction.isLoading ||
    queriesOnFocus.some((query) => query.isLoading || query.isRefetching)

  // queries only return data from props store
  const props = {
    ...elementProps,
    ...assign(queriesOnLoad),
    ...assign(queriesOnFocus),
    ...assign(mutationOnClick),
    ...assign(mutationOnChange),
    ...assign(mutationOnInteraction),
    loading,
    onClick,
    onFocus,
    onChange,
    onInteraction,
  }

  // combine options property for Select, Radio and Checkbox Group
  if (has(props, 'options')) {
    // @ts-expect-error Type error
    props?.options = unionWith(
      assign(queriesOnLoad).options,
      assign(queriesOnFocus).options,
      assign(mutationOnClick).options,
      assign(mutationOnChange).options,
      assign(mutationOnInteraction).options,
      elementProps?.options as [],
      isEqual
    )
  }

  // eslint-disable-next-line react/no-children-prop
  return createElement(Component, {
    ...props,
    name,
    key: name,
    children: node.children,
    validations: node.validations,
  })
}
